import React,{useEffect} from 'react';
import {motion,AnimatePresence,useReducedMotion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {useTheme} from '../../contexts/ThemeContext';

const {FiX}=FiIcons;
const MODAL_EASE=[0.16,1,0.3,1];

const Modal=({isOpen,onClose,title,children,size='md'})=> {
  const {theme}=useTheme();
  const reduceMotion=useReducedMotion();
  const sizeClasses={
    sm:'sm:max-w-sm',
    md:'sm:max-w-xl',
    lg:'sm:max-w-2xl',
    xl:'sm:max-w-4xl'
  };

  useEffect(()=> {
    if (!isOpen) return undefined;

    const originalOverflow=document.body.style.overflow;
    const originalPaddingRight=document.body.style.paddingRight;
    const originalOverscroll=document.body.style.overscrollBehavior;
    const scrollbarWidth=Math.max(0,window.innerWidth-document.documentElement.clientWidth);

    document.body.style.overflow='hidden';
    document.body.style.overscrollBehavior='none';
    if (scrollbarWidth>0) document.body.style.paddingRight=`${scrollbarWidth}px`;

    return ()=> {
      document.body.style.overflow=originalOverflow;
      document.body.style.paddingRight=originalPaddingRight;
      document.body.style.overscrollBehavior=originalOverscroll;
    };
  },[isOpen]);

  useEffect(()=> {
    const handleEscape=(event)=> {
      if (event.key==='Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown',handleEscape);
    return ()=> document.removeEventListener('keydown',handleEscape);
  },[isOpen,onClose]);

  const sheetTransition=reduceMotion
    ? {duration:0.01}
    : {duration:0.34,ease:MODAL_EASE};
  const backdropTransition=reduceMotion
    ? {duration:0.01}
    : {duration:0.24,ease:'easeOut'};

  return (
    <AnimatePresence initial={false} mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-[80] isolate flex items-end sm:items-center justify-center p-0 sm:p-6">
          <motion.button
            type="button"
            tabIndex={-1}
            aria-label="Close modal"
            initial={{opacity:0}}
            animate={{opacity:1}}
            exit={{opacity:0}}
            transition={backdropTransition}
            onClick={onClose}
            className="absolute inset-0 z-0 cursor-default bg-slate-950/80"
          />

          <motion.div
            initial={{opacity:0,y:reduceMotion ? 0 : 42}}
            animate={{opacity:1,y:0}}
            exit={{opacity:0,y:reduceMotion ? 0 : 30}}
            transition={sheetTransition}
            style={{willChange:'transform, opacity'}}
            className={`relative z-10 w-full ${sizeClasses[size]} max-h-[94dvh] sm:max-h-[92dvh] overflow-hidden rounded-t-[1.75rem] rounded-b-none sm:rounded-[2rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.34)] border border-b-0 sm:border-b flex flex-col transform-gpu ${
              theme==='dark' ? 'bg-zinc-950 text-white border-zinc-800' : 'bg-white text-zinc-900 border-zinc-100'
            }`}
          >
            <div className="sm:hidden w-10 h-1 rounded-full bg-zinc-300 dark:bg-zinc-700 mx-auto mt-2.5 flex-shrink-0" aria-hidden="true" />
            <div className={`relative z-20 flex items-center justify-between px-4 py-4 sm:p-8 border-b flex-shrink-0 ${theme==='dark' ? 'bg-zinc-950 border-zinc-900' : 'bg-white border-zinc-100'}`}>
              <h2 className="text-lg sm:text-2xl font-black tracking-tighter truncate pr-4 uppercase">{title}</h2>
              <button
                onClick={onClose}
                className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${theme==='dark' ? 'hover:bg-zinc-900 text-zinc-500' : 'hover:bg-zinc-100 text-zinc-400'}`}
                aria-label="Close"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className={`relative z-10 min-h-0 px-4 py-4 sm:p-8 overflow-x-hidden overflow-y-auto overscroll-contain flex-1 custom-scrollbar pb-[max(1rem,env(safe-area-inset-bottom,1rem))] ${theme==='dark' ? 'bg-zinc-950' : 'bg-white'}`}>
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Modal;